export interface SocietyFeatureNode {
  key: string;
  name: string;
  description: string;
  category: 'DASHBOARD' | 'SOCIETY' | 'FINANCE' | 'COMMUNICATION' | 'OPERATIONS' | 'SECURITY' | 'GOVERNANCE' | 'PROMOTION';
  parentKey?: string;
  defaultEnabled: boolean;
  children?: SocietyFeatureNode[];
}

export interface SocietyFeatureDefinition {
  key: string;
  name: string;
  description: string;
  category: string;
  parentKey?: string;
  defaultEnabled: boolean;
}

export const SOCIETY_FEATURE_TREE: SocietyFeatureNode[] = [
  {
    key: 'dashboard',
    name: 'Dashboard',
    description: 'Society overview metrics, stats, and activity summaries',
    category: 'DASHBOARD',
    defaultEnabled: true,
  },
  {
    key: 'society',
    name: 'Society Management',
    description: 'Society structure, flats, residents, committee, and staff management',
    category: 'SOCIETY',
    defaultEnabled: true,
    children: [
      {
        key: 'society.my_society',
        name: 'My Society',
        description: 'Society profile, branding, and core settings',
        category: 'SOCIETY',
        parentKey: 'society',
        defaultEnabled: true,
      },
      {
        key: 'society.houses',
        name: 'Houses / Units',
        description: 'Manage flats, villas, and unit occupancy status',
        category: 'SOCIETY',
        parentKey: 'society',
        defaultEnabled: true,
      },
      {
        key: 'society.blocks',
        name: 'Blocks & Towers',
        description: 'Block and floor configuration',
        category: 'SOCIETY',
        parentKey: 'society',
        defaultEnabled: true,
      },
      {
        key: 'society.residents',
        name: 'Residents',
        description: 'Resident directory and household member records',
        category: 'SOCIETY',
        parentKey: 'society',
        defaultEnabled: true,
      },
      {
        key: 'society.committee',
        name: 'Committee',
        description: 'Office bearers, executive council, and designations',
        category: 'SOCIETY',
        parentKey: 'society',
        defaultEnabled: true,
      },
      {
        key: 'society.staff',
        name: 'Staff',
        description: 'Security guards, cleaners, technicians, and society employees',
        category: 'SOCIETY',
        parentKey: 'society',
        defaultEnabled: true,
      },
      {
        key: 'society.documents',
        name: 'Documents',
        description: 'Society document vault and bylaws',
        category: 'SOCIETY',
        parentKey: 'society',
        defaultEnabled: true,
      },
      {
        key: 'society.import',
        name: 'Import Data',
        description: 'Bulk Excel/CSV spreadsheet import tool',
        category: 'SOCIETY',
        parentKey: 'society',
        defaultEnabled: true,
      },
    ],
  },
  {
    key: 'finance',
    name: 'Finance',
    description: 'Billing, collections, payments, bank reconciliation, and accounting',
    category: 'FINANCE',
    defaultEnabled: true,
    children: [
      {
        key: 'finance.maintenance',
        name: 'Maintenance Dues & Payments',
        description: 'Monthly dues tracking, owner payment portal, and invoices',
        category: 'FINANCE',
        parentKey: 'finance',
        defaultEnabled: true,
      },
      {
        key: 'finance.collections',
        name: 'Collections',
        description: 'Collection register, offline receipts, and billing cycles',
        category: 'FINANCE',
        parentKey: 'finance',
        defaultEnabled: true,
      },
      {
        key: 'finance.society_fund',
        name: 'Society Fund',
        description: 'General society fund operations and special drives',
        category: 'FINANCE',
        parentKey: 'finance',
        defaultEnabled: true,
      },
      {
        key: 'finance.special_collections',
        name: 'Special Collections',
        description: 'Festival, renovation, and special levy collection drives',
        category: 'FINANCE',
        parentKey: 'finance',
        defaultEnabled: true,
      },
      {
        key: 'finance.corpus_fund',
        name: 'Corpus Fund',
        description: 'Capital reserves, owner contributions, and long-term asset ledger',
        category: 'FINANCE',
        parentKey: 'finance',
        defaultEnabled: true,
      },
      {
        key: 'finance.expenses',
        name: 'Expenses',
        description: 'Vendor bills, expense vouchers, and committee payment approvals',
        category: 'FINANCE',
        parentKey: 'finance',
        defaultEnabled: true,
      },
      {
        key: 'finance.expense_groups',
        name: 'Expense Groups',
        description: 'Expense classification categories and department allocations',
        category: 'FINANCE',
        parentKey: 'finance',
        defaultEnabled: true,
      },
      {
        key: 'finance.water_tanker',
        name: 'Water Tanker',
        description: 'Water delivery logging, automated cost splits, and vendor records',
        category: 'FINANCE',
        parentKey: 'finance',
        defaultEnabled: true,
      },
      {
        key: 'finance.reimbursements',
        name: 'Reimbursements',
        description: 'Committee expense reimbursement claims and settlements',
        category: 'FINANCE',
        parentKey: 'finance',
        defaultEnabled: true,
      },
      {
        key: 'finance.banking',
        name: 'Bank Accounts',
        description: 'Society bank accounts, UPI setup, and ledger balance tracking',
        category: 'FINANCE',
        parentKey: 'finance',
        defaultEnabled: true,
      },
      {
        key: 'finance.receipts',
        name: 'Receipts & Statements',
        description: 'Digital payment receipts, account statements, and PDF generation',
        category: 'FINANCE',
        parentKey: 'finance',
        defaultEnabled: true,
      },
      {
        key: 'finance.reports',
        name: 'Reports',
        description: 'Income & expenditure statements, balance sheets, and audit ledgers',
        category: 'FINANCE',
        parentKey: 'finance',
        defaultEnabled: true,
      },
      {
        key: 'finance.reconciliation',
        name: 'Payment Reconciliation',
        description: 'Gateway settlement reconciliation and bank statement match engine',
        category: 'FINANCE',
        parentKey: 'finance',
        defaultEnabled: true,
      },
    ],
  },
  {
    key: 'communication',
    name: 'Communication',
    description: 'Notices, community forum, events, invitations, and WhatsApp notifications',
    category: 'COMMUNICATION',
    defaultEnabled: true,
    children: [
      {
        key: 'communication.notices',
        name: 'Notices',
        description: 'Circular broadcasting, announcements, and emergency notices',
        category: 'COMMUNICATION',
        parentKey: 'communication',
        defaultEnabled: true,
      },
      {
        key: 'communication.forum',
        name: 'Community Forum',
        description: 'Discussion forum, resident threads, and community discussions',
        category: 'COMMUNICATION',
        parentKey: 'communication',
        defaultEnabled: true,
      },
      {
        key: 'communication.events',
        name: 'Events & Calendar',
        description: 'Society gatherings, festival celebrations, and event ticketing',
        category: 'COMMUNICATION',
        parentKey: 'communication',
        defaultEnabled: true,
      },
      {
        key: 'communication.invitations',
        name: 'Invitations & RSVPs',
        description: 'Digital guest invitations and RSVP confirmation desk',
        category: 'COMMUNICATION',
        parentKey: 'communication',
        defaultEnabled: true,
      },
      {
        key: 'communication.whatsapp',
        name: 'WhatsApp Logs',
        description: 'Automated WhatsApp payment alerts and notification logs',
        category: 'COMMUNICATION',
        parentKey: 'communication',
        defaultEnabled: true,
      },
    ],
  },
  {
    key: 'operations',
    name: 'Operations & Helpdesk',
    description: 'Complaints, service requests, amenities, meetings, and documents',
    category: 'OPERATIONS',
    defaultEnabled: true,
    children: [
      {
        key: 'operations.complaints',
        name: 'Complaints Desk',
        description: 'Resident ticketing, auto-assignment, and resolution tracking',
        category: 'OPERATIONS',
        parentKey: 'operations',
        defaultEnabled: true,
      },
      {
        key: 'operations.services',
        name: 'Service Requests',
        description: 'Maintenance service requests and local vendor directory',
        category: 'OPERATIONS',
        parentKey: 'operations',
        defaultEnabled: true,
      },
      {
        key: 'operations.amenities',
        name: 'Amenities & Facilities',
        description: 'Clubhouse, sports courts, and party hall booking system',
        category: 'OPERATIONS',
        parentKey: 'operations',
        defaultEnabled: true,
      },
      {
        key: 'operations.meetings',
        name: 'Meetings',
        description: 'AGM/EGM scheduling, digital minutes, and quorum tracking',
        category: 'OPERATIONS',
        parentKey: 'operations',
        defaultEnabled: true,
      },
      {
        key: 'operations.documents',
        name: 'Document Vault',
        description: 'Centralized document repository and society archive',
        category: 'OPERATIONS',
        parentKey: 'operations',
        defaultEnabled: true,
      },
    ],
  },
  {
    key: 'security',
    name: 'Security & Gate',
    description: 'Visitor logs, gate passes, watchman shifts, and CC camera integration',
    category: 'SECURITY',
    defaultEnabled: true,
    children: [
      {
        key: 'security.visitors',
        name: 'Visitors Desk',
        description: 'Visitor management, entry/exit logs, and delivery tracking',
        category: 'SECURITY',
        parentKey: 'security',
        defaultEnabled: true,
      },
      {
        key: 'security.gate_pass',
        name: 'Gate Pass',
        description: 'Resident-issued QR gate passes and visitor pre-approvals',
        category: 'SECURITY',
        parentKey: 'security',
        defaultEnabled: true,
      },
      {
        key: 'security.cc_cameras',
        name: 'CC Cameras',
        description: 'Live CCTV surveillance streams and perimeter camera feeds',
        category: 'SECURITY',
        parentKey: 'security',
        defaultEnabled: false, // Safely defaults to OFF for newly introduced feature
      },
      {
        key: 'security.gates',
        name: 'Gate Management',
        description: 'Multi-gate setup, barriers, and checkpoint configurations',
        category: 'SECURITY',
        parentKey: 'security',
        defaultEnabled: true,
      },
      {
        key: 'security.shifts',
        name: 'Watchman Shifts',
        description: 'Guard duty rosters, patrol checkpoints, and shift reports',
        category: 'SECURITY',
        parentKey: 'security',
        defaultEnabled: true,
      },
      {
        key: 'security.policies',
        name: 'Security Policies',
        description: 'Gate entry policies, night lockdown rules, and security policies',
        category: 'SECURITY',
        parentKey: 'security',
        defaultEnabled: true,
      },
    ],
  },
  {
    key: 'elections',
    name: 'Elections & Governance',
    description: 'Society board elections, voting, resident polls, and petitions',
    category: 'GOVERNANCE',
    defaultEnabled: true,
    children: [
      {
        key: 'elections.setup',
        name: 'Election Setup',
        description: 'Election terms, nomination schedules, and voter list verification',
        category: 'GOVERNANCE',
        parentKey: 'elections',
        defaultEnabled: true,
      },
      {
        key: 'elections.voting',
        name: 'Voting',
        description: 'Cryptographic secret ballot voting and tallying',
        category: 'GOVERNANCE',
        parentKey: 'elections',
        defaultEnabled: true,
      },
      {
        key: 'elections.results',
        name: 'Results',
        description: 'Election tallying, certificates, and winning committee records',
        category: 'GOVERNANCE',
        parentKey: 'elections',
        defaultEnabled: true,
      },
      {
        key: 'elections.polls',
        name: 'Resident Polls',
        description: 'Informal sentiment surveys and resident satisfaction polls',
        category: 'GOVERNANCE',
        parentKey: 'elections',
        defaultEnabled: true,
      },
      {
        key: 'elections.petitions',
        name: 'Petitions',
        description: 'Formal resident petitions and endorsements',
        category: 'GOVERNANCE',
        parentKey: 'elections',
        defaultEnabled: true,
      },
      {
        key: 'elections.resignation',
        name: 'Resignations',
        description: 'Formal office bearer resignation submission and withdrawal',
        category: 'GOVERNANCE',
        parentKey: 'elections',
        defaultEnabled: true,
      },
    ],
  },
  {
    key: 'promotion',
    name: 'Promotion',
    description: 'Marketing campaigns and prospect outreach',
    category: 'PROMOTION',
    defaultEnabled: true,
  },
];

export const ALL_SOCIETY_FEATURES: Record<string, SocietyFeatureDefinition> = {};

function registerNodes(nodes: SocietyFeatureNode[], parentKey?: string) {
  for (const node of nodes) {
    ALL_SOCIETY_FEATURES[node.key] = {
      key: node.key,
      name: node.name,
      description: node.description,
      category: node.category,
      parentKey: node.parentKey || parentKey,
      defaultEnabled: node.defaultEnabled,
    };
    if (node.children?.length) {
      registerNodes(node.children, node.key);
    }
  }
}

registerNodes(SOCIETY_FEATURE_TREE);

export const PLAN_DEFAULT_FEATURE_KEYS: Record<string, string[]> = {
  STARTER: [
    'dashboard',
    'society',
    'society.my_society',
    'society.houses',
    'society.residents',
    'society.committee',
    'society.staff',
    'communication',
    'communication.notices',
    'communication.email',
    'operations',
    'operations.helpdesk',
    'operations.meetings',
    'operations.visitors',
    'operations.calendar',
    'security',
    'security.visitor_security',
    'security.watchman',
    'promotion',
  ],
  PRO: [
    'dashboard',
    'society',
    'society.my_society',
    'society.houses',
    'society.blocks',
    'society.residents',
    'society.committee',
    'society.staff',
    'society.documents',
    'society.import',
    'finance',
    'finance.general_ledger',
    'finance.maintenance_invoicing',
    'finance.expenses',
    'finance.bank_accounts',
    'finance.financial_reports',
    'communication',
    'communication.notices',
    'communication.email',
    'communication.whatsapp',
    'communication.forum',
    'operations',
    'operations.helpdesk',
    'operations.meetings',
    'operations.visitors',
    'operations.calendar',
    'operations.amenities',
    'security',
    'security.visitor_security',
    'security.watchman',
    'security.qr_gate_pass',
    'elections',
    'elections.elections',
    'elections.secret_ballot',
    'promotion',
  ],
  PLUS: [
    'dashboard',
    'society',
    'society.my_society',
    'society.houses',
    'society.blocks',
    'society.residents',
    'society.committee',
    'society.staff',
    'society.documents',
    'society.import',
    'finance',
    'finance.general_ledger',
    'finance.maintenance_invoicing',
    'finance.expenses',
    'finance.bank_accounts',
    'finance.financial_reports',
    'communication',
    'communication.notices',
    'communication.email',
    'communication.whatsapp',
    'communication.forum',
    'operations',
    'operations.helpdesk',
    'operations.meetings',
    'operations.visitors',
    'operations.calendar',
    'operations.amenities',
    'security',
    'security.visitor_security',
    'security.watchman',
    'security.qr_gate_pass',
    'elections',
    'elections.elections',
    'elections.secret_ballot',
    'promotion',
  ],
  PROFESSIONAL: [
    'dashboard',
    'society',
    'society.my_society',
    'society.houses',
    'society.blocks',
    'society.residents',
    'society.committee',
    'society.staff',
    'society.documents',
    'society.import',
    'finance',
    'finance.general_ledger',
    'finance.maintenance_invoicing',
    'finance.expenses',
    'finance.bank_accounts',
    'finance.financial_reports',
    'communication',
    'communication.notices',
    'communication.email',
    'communication.whatsapp',
    'communication.forum',
    'operations',
    'operations.helpdesk',
    'operations.meetings',
    'operations.visitors',
    'operations.calendar',
    'operations.amenities',
    'security',
    'security.visitor_security',
    'security.watchman',
    'security.qr_gate_pass',
    'elections',
    'elections.elections',
    'elections.secret_ballot',
    'promotion',
  ],
  ENTERPRISE: Object.keys(ALL_SOCIETY_FEATURES),
  FREE_TRIAL: Object.keys(ALL_SOCIETY_FEATURES).filter((k) => k !== 'security.cc_cameras'),
};

/**
 * Returns the list of dot-notation feature keys included in a plan.
 */
export function getPlanIncludedFeatureKeys(planCode?: string | null): string[] {
  if (!planCode) return PLAN_DEFAULT_FEATURE_KEYS.PRO;
  const normalized = planCode.trim().toUpperCase();
  if (PLAN_DEFAULT_FEATURE_KEYS[normalized]) {
    return PLAN_DEFAULT_FEATURE_KEYS[normalized];
  }
  if (normalized.includes('ENTERPRISE')) return PLAN_DEFAULT_FEATURE_KEYS.ENTERPRISE;
  if (normalized.includes('STARTER')) return PLAN_DEFAULT_FEATURE_KEYS.STARTER;
  if (normalized.includes('TRIAL')) return PLAN_DEFAULT_FEATURE_KEYS.FREE_TRIAL;
  return PLAN_DEFAULT_FEATURE_KEYS.PRO;
}

export const DEFAULT_SOCIETY_FEATURES: Record<string, boolean> = {};
for (const [key, def] of Object.entries(ALL_SOCIETY_FEATURES)) {
  DEFAULT_SOCIETY_FEATURES[key] = def.defaultEnabled;
}

export interface SocietyAuthMethodsConfig {
  email_password: boolean;
  mobile_email_otp: boolean;
  mobile_password: boolean;
}

export const DEFAULT_AUTH_METHODS: SocietyAuthMethodsConfig = {
  email_password: true,
  mobile_email_otp: true,
  mobile_password: true,
};

/**
 * Resolves whether a feature key is effectively enabled for a society.
 * Handles hierarchical parent-child inheritance.
 */
export function isFeatureEffective(
  features: Record<string, boolean> | undefined,
  featureKey: string,
): boolean {
  if (!features) {
    return ALL_SOCIETY_FEATURES[featureKey]?.defaultEnabled ?? true;
  }

  const def = ALL_SOCIETY_FEATURES[featureKey];
  if (def?.parentKey) {
    const parentVal = features[def.parentKey];
    const isParentEnabled =
      parentVal !== undefined
        ? Boolean(parentVal)
        : (ALL_SOCIETY_FEATURES[def.parentKey]?.defaultEnabled ?? true);
    if (!isParentEnabled) {
      return false;
    }
  }

  const configured = features[featureKey];
  if (configured !== undefined) {
    return Boolean(configured);
  }

  return def?.defaultEnabled ?? true;
}
